/**
 * BlogVisual.jsx — Visualizaciones inline para posts del Blog
 * Renderiza diagramas SVG/Tailwind sin dependencias externas.
 * Cada tipo se activa con el campo `visual.type` en blogPosts.js
 */

import React from 'react';

// ─────────────────────────────────────────────
// 1. CICLO CREAR-MEDIR-APRENDER
// ─────────────────────────────────────────────
const BuildMeasureLearnCycle = () => (
  <div className="my-8 rounded-2xl bg-gradient-to-br from-slate-900 to-[#0c2240] p-8 flex flex-col items-center overflow-hidden">
    <p className="text-white/60 text-xs uppercase tracking-widest mb-6 font-semibold">El motor del Lean Startup</p>
    <div className="relative w-full max-w-sm mx-auto" style={{ height: 280 }}>
      {/* SVG circular arrows */}
      <svg viewBox="0 0 280 280" className="w-full h-full absolute inset-0">
        {/* Arco izquierdo: CREAR → MEDIR */}
        <path
          d="M 140 40 A 100 100 0 0 1 227 190"
          fill="none" stroke="#22d3ee" strokeWidth="3" strokeLinecap="round"
          strokeDasharray="8 4"
        />
        {/* flecha CREAR→MEDIR */}
        <polygon points="227,190 215,178 235,175" fill="#22d3ee" />

        {/* Arco inferior: MEDIR → APRENDER */}
        <path
          d="M 227 190 A 100 100 0 0 1 53 190"
          fill="none" stroke="#a78bfa" strokeWidth="3" strokeLinecap="round"
          strokeDasharray="8 4"
        />
        {/* flecha MEDIR→APRENDER */}
        <polygon points="53,190 65,178 45,175" fill="#a78bfa" />

        {/* Arco derecho: APRENDER → CREAR */}
        <path
          d="M 53 190 A 100 100 0 0 1 140 40"
          fill="none" stroke="#34d399" strokeWidth="3" strokeLinecap="round"
          strokeDasharray="8 4"
        />
        {/* flecha APRENDER→CREAR */}
        <polygon points="140,40 128,52 148,58" fill="#34d399" />
      </svg>

      {/* Nodo CREAR (arriba) */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 flex flex-col items-center">
        <div className="w-16 h-16 rounded-full bg-cyan-500 flex items-center justify-center shadow-lg shadow-cyan-500/30">
          <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </div>
        <span className="mt-2 text-cyan-300 font-bold text-sm tracking-wide uppercase">Crear</span>
        <span className="text-white/50 text-xs">Producto / PMV</span>
      </div>

      {/* Nodo MEDIR (derecha) */}
      <div className="absolute right-0 bottom-8 flex flex-col items-center">
        <div className="w-16 h-16 rounded-full bg-violet-500 flex items-center justify-center shadow-lg shadow-violet-500/30">
          <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
        <span className="mt-2 text-violet-300 font-bold text-sm tracking-wide uppercase">Medir</span>
        <span className="text-white/50 text-xs">Datos reales</span>
      </div>

      {/* Nodo APRENDER (izquierda) */}
      <div className="absolute left-0 bottom-8 flex flex-col items-center">
        <div className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
          <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        </div>
        <span className="mt-2 text-emerald-300 font-bold text-sm tracking-wide uppercase">Aprender</span>
        <span className="text-white/50 text-xs">Validado o pivotar</span>
      </div>
    </div>

    <div className="mt-6 grid grid-cols-3 gap-3 w-full max-w-md text-center">
      <div className="bg-white/5 rounded-lg p-3">
        <p className="text-cyan-300 font-semibold text-xs mb-1">CREAR</p>
        <p className="text-white/60 text-xs leading-snug">Convierte la idea en un PMV testable</p>
      </div>
      <div className="bg-white/5 rounded-lg p-3">
        <p className="text-violet-300 font-semibold text-xs mb-1">MEDIR</p>
        <p className="text-white/60 text-xs leading-snug">Datos de clientes reales, no vanidosos</p>
      </div>
      <div className="bg-white/5 rounded-lg p-3">
        <p className="text-emerald-300 font-semibold text-xs mb-1">APRENDER</p>
        <p className="text-white/60 text-xs leading-snug">Pivotar o perseverar con evidencia</p>
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────
// 2. PMV EN 3 CASOS (Zappos, Dropbox, Groupon)
// ─────────────────────────────────────────────
const PmvCases = () => {
  const cases = [
    {
      company: 'Zappos',
      color: 'from-orange-500 to-amber-500',
      bg: 'bg-orange-50 border-orange-200',
      headerText: 'text-orange-700',
      hypothesis: '¿Comprará la gente zapatos online sin probárselos?',
      pmv: 'Fotos de zapaterias locales en una web simple. Si alguien pedía, compraba el par al precio normal y lo enviaba.',
      cost: '~$0',
      learned: 'Validó la demanda antes de construir infraestructura',
      icon: '👟',
    },
    {
      company: 'Dropbox',
      color: 'from-blue-500 to-cyan-500',
      bg: 'bg-blue-50 border-blue-200',
      headerText: 'text-blue-700',
      hypothesis: '¿Existe demanda real de sincronización de archivos?',
      pmv: 'Vídeo de 3 minutos demostrando cómo funcionaría el producto. Ninguna línea de código funcional.',
      cost: '1 día de grabación',
      learned: 'Lista de espera: de 5.000 a 75.000 en una noche',
      icon: '📦',
    },
    {
      company: 'Groupon',
      color: 'from-green-500 to-emerald-500',
      bg: 'bg-green-50 border-green-200',
      headerText: 'text-green-700',
      hypothesis: '¿Se apuntará gente a ofertas diarias de negocios locales?',
      pmv: 'Blog WordPress. Una oferta al día. Cupones como PDFs generados manualmente uno a uno.',
      cost: 'Horas de trabajo manual',
      learned: 'El modelo funcionaba antes de automatizar nada',
      icon: '🏷️',
    },
  ];

  return (
    <div className="my-8">
      <div className="text-center mb-6">
        <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">El PMV en la práctica</span>
        <h3 className="text-lg font-bold text-slate-800 mt-1">Tres casos, el mismo principio: aprender antes de construir</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {cases.map((c) => (
          <div key={c.company} className={`rounded-xl border ${c.bg} overflow-hidden`}>
            <div className={`bg-gradient-to-r ${c.color} px-4 py-3 flex items-center gap-3`}>
              <span className="text-2xl">{c.icon}</span>
              <span className="text-white font-bold text-lg">{c.company}</span>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <p className={`text-xs font-semibold uppercase tracking-wide ${c.headerText} mb-1`}>Hipótesis</p>
                <p className="text-slate-700 text-sm leading-snug">{c.hypothesis}</p>
              </div>
              <div>
                <p className={`text-xs font-semibold uppercase tracking-wide ${c.headerText} mb-1`}>PMV real</p>
                <p className="text-slate-700 text-sm leading-snug">{c.pmv}</p>
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                <div>
                  <p className="text-xs text-slate-500">Coste</p>
                  <p className={`font-bold text-sm ${c.headerText}`}>{c.cost}</p>
                </div>
              </div>
              <div className="bg-white/70 rounded-lg p-3">
                <p className="text-xs text-slate-600 leading-snug">
                  <span className="font-semibold">Aprendizaje:</span> {c.learned}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// 3. LOS 4 PASOS DEL DESARROLLO DE CLIENTES
// ─────────────────────────────────────────────
const CustomerDevelopmentSteps = () => {
  const steps = [
    {
      num: '1',
      name: 'Descubrimiento',
      subtitle: 'de Clientes',
      desc: 'Transformar hipótesis en hechos. Salir a la calle sin producto. ¿Es real el problema?',
      color: 'bg-cyan-600',
      light: 'bg-cyan-50 border-cyan-200 text-cyan-800',
    },
    {
      num: '2',
      name: 'Validación',
      subtitle: 'de Clientes',
      desc: 'Crear un proceso de venta repetible. La gente saca la cartera y paga.',
      color: 'bg-blue-600',
      light: 'bg-blue-50 border-blue-200 text-blue-800',
    },
    {
      num: '3',
      name: 'Creación',
      subtitle: 'de Clientes',
      desc: 'Solo si superaste la validación: pisar el acelerador del marketing y las ventas.',
      color: 'bg-violet-600',
      light: 'bg-violet-50 border-violet-200 text-violet-800',
    },
    {
      num: '4',
      name: 'Construcción',
      subtitle: 'de la Empresa',
      desc: 'Pasar de máquina de aprender a máquina de ejecutar. Sobre base sólida.',
      color: 'bg-emerald-600',
      light: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    },
  ];

  return (
    <div className="my-8">
      <div className="text-center mb-6">
        <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Steve Blank</span>
        <h3 className="text-lg font-bold text-slate-800 mt-1">Los 4 pasos del Desarrollo de Clientes</h3>
        <p className="text-sm text-slate-500 mt-1">Un ciclo, no una línea recta. Volver atrás es aprendizaje validado, no fracaso.</p>
      </div>

      {/* Desktop: horizontal */}
      <div className="hidden sm:flex items-stretch gap-0">
        {steps.map((s, i) => (
          <React.Fragment key={s.num}>
            <div className={`flex-1 rounded-xl border ${s.light} p-5 flex flex-col`}>
              <div className={`w-10 h-10 rounded-full ${s.color} text-white flex items-center justify-center font-bold text-lg mb-3 shadow-md`}>
                {s.num}
              </div>
              <p className="font-bold text-slate-900 text-base leading-tight">{s.name}</p>
              <p className="text-slate-500 text-sm mb-3">{s.subtitle}</p>
              <p className="text-slate-600 text-sm leading-snug flex-1">{s.desc}</p>
            </div>
            {i < steps.length - 1 && (
              <div className="flex items-center px-1">
                <svg className="w-6 h-6 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Mobile: vertical */}
      <div className="sm:hidden space-y-3">
        {steps.map((s, i) => (
          <div key={s.num} className={`rounded-xl border ${s.light} p-4 flex gap-4`}>
            <div className={`w-10 h-10 rounded-full ${s.color} text-white flex items-center justify-center font-bold text-lg flex-shrink-0 shadow-md`}>
              {s.num}
            </div>
            <div>
              <p className="font-bold text-slate-900">{s.name} <span className="text-slate-500 font-normal text-sm">{s.subtitle}</span></p>
              <p className="text-slate-600 text-sm mt-1 leading-snug">{s.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
        <p className="text-amber-800 text-sm leading-snug">
          <span className="font-bold">Regla de Blank:</span> Solo si superas la fase 2 de Validación pasas a la 3. Escalar antes de validar es el error que mató a Webvan.
        </p>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// 4. LEAN CANVAS — 9 BLOQUES
// ─────────────────────────────────────────────
const LeanCanvasGrid = () => {
  const blocks = [
    { num: 1, name: 'Problema', desc: '3 problemas principales del segmento + alternativas actuales', color: 'border-red-400 bg-red-50', badge: 'bg-red-500' },
    { num: 2, name: 'Segmento de Clientes', desc: 'Grupo concreto + early adopters (los que ya improvisan soluciones)', color: 'border-orange-400 bg-orange-50', badge: 'bg-orange-500' },
    { num: 3, name: 'Propuesta de Valor Única', desc: 'Titular de periódico: qué te hace diferente en una frase', color: 'border-yellow-400 bg-yellow-50', badge: 'bg-yellow-500' },
    { num: 4, name: 'Solución', desc: 'Solo 3-4 características clave. Solo después de validar el problema.', color: 'border-green-400 bg-green-50', badge: 'bg-green-500' },
    { num: 5, name: 'Canales', desc: 'Cómo llegas al cliente: marketing, ventas, distribución', color: 'border-teal-400 bg-teal-50', badge: 'bg-teal-500' },
    { num: 6, name: 'Flujos de Ingresos', desc: 'Modelo de monetización + precio (parte del producto, no una nota al pie)', color: 'border-cyan-400 bg-cyan-50', badge: 'bg-cyan-500' },
    { num: 7, name: 'Estructura de Costes', desc: 'Servidores, salarios, marketing. Los costes reales del modelo.', color: 'border-blue-400 bg-blue-50', badge: 'bg-blue-500' },
    { num: 8, name: 'Métricas Clave', desc: '1-2 números que dicen la verdad: ¿avanza el barco o se hunde?', color: 'border-violet-400 bg-violet-50', badge: 'bg-violet-500' },
    { num: 9, name: 'Ventaja Injusta', desc: 'Lo que no puedes copiar ni comprar: comunidad, datos, confianza de marca, conocimiento del cliente', color: 'border-purple-400 bg-purple-50', badge: 'bg-purple-500' },
  ];

  return (
    <div className="my-8">
      <div className="text-center mb-6">
        <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Ash Maurya — Running Lean</span>
        <h3 className="text-lg font-bold text-slate-800 mt-1">Lean Canvas: 9 bloques en una sola hoja</h3>
        <p className="text-sm text-slate-500 mt-1">El número indica el orden correcto de relleno. Empieza siempre por el problema, nunca por la solución.</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {blocks.map((b) => (
          <div key={b.num} className={`rounded-xl border-2 ${b.color} p-4`}>
            <div className="flex items-center gap-2 mb-2">
              <span className={`w-6 h-6 rounded-full ${b.badge} text-white text-xs font-bold flex items-center justify-center flex-shrink-0`}>
                {b.num}
              </span>
              <span className="font-semibold text-slate-800 text-sm leading-tight">{b.name}</span>
            </div>
            <p className="text-slate-600 text-xs leading-snug">{b.desc}</p>
          </div>
        ))}
      </div>
      <div className="mt-4 bg-slate-800 rounded-xl p-4">
        <p className="text-white/80 text-sm text-center leading-snug">
          <span className="text-white font-bold">Error número uno:</span> Empezar por el bloque 4 (Solución). Maurya obliga a ser detective antes que inventor. El problema primero, siempre.
        </p>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// 5. PIVOTAR vs OPTIMIZAR
// ─────────────────────────────────────────────
const PivotVsOptimize = () => (
  <div className="my-8">
    <div className="text-center mb-6">
      <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Running Lean</span>
      <h3 className="text-lg font-bold text-slate-800 mt-1">Pivotar vs. Optimizar: dónde pones la energía</h3>
      <p className="text-sm text-slate-500 mt-1">La distinción cambia por completo cómo trabaja el equipo según la fase</p>
    </div>

    {/* Divisor visual: antes/después del PMF */}
    <div className="flex items-center gap-2 mb-4">
      <div className="flex-1 h-px bg-slate-200" />
      <div className="flex items-center gap-2 bg-amber-100 border border-amber-300 rounded-full px-4 py-1">
        <svg className="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 100 18A9 9 0 0012 3z" />
        </svg>
        <span className="text-amber-700 font-semibold text-xs uppercase tracking-wide">Product-Market Fit</span>
      </div>
      <div className="flex-1 h-px bg-slate-200" />
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {/* ANTES */}
      <div className="rounded-xl border-2 border-violet-200 bg-violet-50 overflow-hidden">
        <div className="bg-violet-600 px-5 py-3">
          <p className="text-white font-bold text-base">Antes del PMF</p>
          <p className="text-violet-200 text-sm">Modo: Aprender</p>
        </div>
        <div className="p-5 space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-4 h-4 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
            <div>
              <p className="font-semibold text-slate-800 text-sm">Objetivo: aprender</p>
              <p className="text-slate-600 text-xs mt-1">No es ganar dinero todavía. Es descubrir si el modelo funciona.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-4 h-4 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <div>
              <p className="font-semibold text-slate-800 text-sm">Herramienta: pivot</p>
              <p className="text-slate-600 text-xs mt-1">Corrección de rumbo estructurada basada en datos de experimentos. No es una ocurrencia.</p>
            </div>
          </div>
          <div className="bg-violet-100 rounded-lg p-3 mt-2">
            <p className="text-violet-800 text-xs italic">"Antes de encontrar la veta, explorar y cambiar de sitio."</p>
          </div>
        </div>
      </div>

      {/* DESPUÉS */}
      <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 overflow-hidden">
        <div className="bg-emerald-600 px-5 py-3">
          <p className="text-white font-bold text-base">Después del PMF</p>
          <p className="text-emerald-200 text-sm">Modo: Crecer</p>
        </div>
        <div className="p-5 space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
              </svg>
            </div>
            <div>
              <p className="font-semibold text-slate-800 text-sm">Objetivo: crecer</p>
              <p className="text-slate-600 text-xs mt-1">Ya tienes evidencia de que funciona. Ahora escala lo que ya demostró resultados.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
            </div>
            <div>
              <p className="font-semibold text-slate-800 text-sm">Herramienta: optimizar</p>
              <p className="text-slate-600 text-xs mt-1">Pequeños ajustes para acelerar: +5% conversión, -10% coste adquisición.</p>
            </div>
          </div>
          <div className="bg-emerald-100 rounded-lg p-3 mt-2">
            <p className="text-emerald-800 text-xs italic">"Una vez encontrada la veta, construir la mina más eficiente."</p>
          </div>
        </div>
      </div>
    </div>

    <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
      <p className="text-red-800 text-sm leading-snug text-center">
        <span className="font-bold">El error fatal:</span> Escalar (usar herramientas de la derecha) antes de alcanzar el PMF. Es añadir gasolina a un fuego que todavía no está encendido. El camino más rápido a la quiebra.
      </p>
    </div>
  </div>
);

// ─────────────────────────────────────────────
// DISPATCHER PRINCIPAL
// ─────────────────────────────────────────────
const BlogVisual = ({ type }) => {
  switch (type) {
    case 'build-measure-learn':
      return <BuildMeasureLearnCycle />;
    case 'pmv-cases':
      return <PmvCases />;
    case 'customer-development-steps':
      return <CustomerDevelopmentSteps />;
    case 'lean-canvas':
      return <LeanCanvasGrid />;
    case 'pivot-vs-optimize':
      return <PivotVsOptimize />;
    default:
      return null;
  }
};

export default BlogVisual;
